import React, { useState, useRef, useEffect } from 'react';
import { getApiBaseUrl } from '../utils/apiConfig';

const EnhancedMathChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const savedHistory = localStorage.getItem('enhanced_mathbot_history');
    if (savedHistory) {
      const parsed = JSON.parse(savedHistory);
      return parsed.map(msg => ({ ...msg, time: new Date(msg.time) }));
    }
    return [
      { 
        type: 'bot', 
        text: 'Hello! 🤖 I\'m MathVenture_AI, your intelligent math companion! I can solve math problems in English and Nepali and understand various question formats. Ask me anything about mathematics! 🧮✨', 
        time: new Date() 
      }
    ];
  });
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState('en');
  const messagesEndRef = useRef(null);

  // Mathematical patterns for English and Nepali only
  const mathPatterns = {
    // Unit conversions
    unitConversion: {
      patterns: [
        // Volume conversions
        /how\s+many\s+milliliters?\s+(?:are\s+)?in\s+(\d+(?:\.\d+)?)\s+liters?/i,
        /(\d+(?:\.\d+)?)\s+liters?\s+(?:to|in)\s+milliliters?/i,
        /(\d+(?:\.\d+)?)\s+liters?\s+(?:=|equals?)\s+how\s+many\s+ml/i,
        /(\d+(?:\.\d+)?)\s+l\s+to\s+ml/i,
        /convert\s+(\d+(?:\.\d+)?)\s+liters?\s+to\s+ml/i,
        
        // Reverse: ml to liters
        /how\s+many\s+liters?\s+(?:are\s+)?in\s+(\d+(?:\.\d+)?)\s+milliliters?/i,
        /(\d+(?:\.\d+)?)\s+milliliters?\s+(?:to|in)\s+liters?/i,
        /(\d+(?:\.\d+)?)\s+ml\s+to\s+l/i,
        /convert\s+(\d+(?:\.\d+)?)\s+ml\s+to\s+liters?/i,
        
        // Length conversions
        /(\d+(?:\.\d+)?)\s+meters?\s+(?:to|in)\s+centimeters?/i,
        /(\d+(?:\.\d+)?)\s+cm\s+to\s+mm/i,
        /(\d+(?:\.\d+)?)\s+kg\s+to\s+g/i,
        
        // Nepali unit conversions
        /(\d+(?:\.\d+)?)\s+liter\s+ma\s+kati\s+ml/i,
        /(\d+(?:\.\d+)?)\s+ml\s+ma\s+kati\s+liter/i
      ],
      operation: (value, fromUnit, toUnit) => {
        const conversions = {
          'liters_to_ml': (v) => v * 1000,
          'ml_to_liters': (v) => v / 1000,
          'meters_to_cm': (v) => v * 100,
          'cm_to_mm': (v) => v * 10,
          'kg_to_g': (v) => v * 1000
        };
        return conversions[`${fromUnit}_to_${toUnit}`]?.(value);
      }
    },
    
    // Pattern recognition
    patterns: {
      patterns: [
        /what\s+comes\s+next\s+in\s+(?:the\s+)?pattern[:\s]*(\d+(?:\s*,\s*\d+)*)/i,
        /pattern[:\s]*(\d+(?:\s*,\s*\d+)*)\s*,?\s*___?\s*\??/i,
        /find\s+(?:the\s+)?next\s+number[:\s]*(\d+(?:\s*,\s*\d+)*)/i,
        /(\d+(?:\s*,\s*\d+)*)\s*,?\s*___?\s*what\s+comes\s+next/i,
        /sequence[:\s]*(\d+(?:\s*,\s*\d+)*)/i,
        
        // Nepali patterns
        /(\d+(?:\s*,\s*\d+)*)\s*ma\s+arko\s+number\s+k\s+ho/i,
        /pattern\s+ma\s+(\d+(?:\s*,\s*\d+)*)\s+pachhi\s+k\s+aaucha/i
      ],
      operation: (numbersStr) => {
        const numbers = numbersStr.split(',').map(n => parseInt(n.trim()));
        return findPattern(numbers);
      }
    },

    // Basic arithmetic patterns in English and Nepali
    addition: {
      patterns: [
        // English
        /what\s+is\s+(\d+)\s*(?:\+|plus|add(?:ed)?(?:\s+to)?)\s*(\d+)/i,
        /(\d+)\s*(?:\+|plus|add(?:ed)?(?:\s+to)?)\s*(\d+)\s*(?:=|\?|equals?)/i,
        /add\s+(\d+)\s+(?:and|to)\s+(\d+)/i,
        /sum\s+of\s+(\d+)\s+and\s+(\d+)/i,
        
        // Nepali (Romanized)
        /(\d+)\s+ra\s+(\d+)\s+jodda\s+kati/i,
        /(\d+)\s+ma\s+(\d+)\s+jodyo\s+bhane/i,
        /(\d+)\s*\+\s*(\d+)\s+kati/i,
        /(\d+)\s+\+\s+(\d+)\s+kati/i,
        /(\d+)\s+ra\s+(\d+)\s+kati/i,
        /(\d+)\s+(\d+)\s+jod\s+kati/i
      ],
      operation: (a, b) => a + b,
      symbol: '+'
    },
    
    subtraction: {
      patterns: [
        // English
        /what\s+is\s+(\d+)\s*(?:-|minus|subtract(?:ed)?(?:\s+from)?)\s*(\d+)/i,
        /(\d+)\s*(?:-|minus|subtract(?:ed)?(?:\s+from)?)\s*(\d+)\s*(?:=|\?|equals?)/i,
        /difference\s+(?:between\s+)?(\d+)\s+and\s+(\d+)/i,
        /(\d+)\s+take\s+away\s+(\d+)/i,
        
        // Nepali (Romanized)
        /(\d+)\s+ma\s+(\d+)\s+ghatayo\s+bhane/i,
        /(\d+)\s+dekhi\s+(\d+)\s+ghatda/i,
        /(\d+)\s*-\s*(\d+)\s+kati/i,
        /(\d+)\s+ma\s+(\d+)\s+ghatda\s+kati/i
      ],
      operation: (a, b) => a - b,
      symbol: '-'
    },
    
    multiplication: {
      patterns: [
        // English
        /what\s+is\s+(\d+)\s*(?:[×*x]|times?|multipl(?:y|ied)\s*by)\s*(\d+)/i,
        /(\d+)\s*(?:[×*x]|times?|multipl(?:y|ied)\s*by)\s*(\d+)\s*(?:=|\?|equals?)/i,
        /(\d+)\s+time\s+(\d+)/i,
        /(\d+)\s+times\s+(\d+)/i,
        /product\s+of\s+(\d+)\s+and\s+(\d+)/i,
        /(\d+)\s+groups?\s+of\s+(\d+)/i,
        
        // Nepali (Romanized)
        /(\d+)\s+ra\s+(\d+)\s+ko\s+guna/i,
        /(\d+)\s+lai\s+(\d+)\s+le\s+guna\s+garda/i,
        /(\d+)\s*×\s*(\d+)\s+kati/i,
        /(\d+)\s+(\d+)\s+guna\s+kati/i,
        /(\d+)\s+ko\s+(\d+)\s+guna/i
      ],
      operation: (a, b) => a * b,
      symbol: '×'
    },
    
    division: {
      patterns: [
        // English
        /what\s+is\s+(\d+)\s*(?:[÷/]|divided\s*by)\s*(\d+)/i,
        /(\d+)\s*(?:[÷/]|divided\s*by)\s*(\d+)\s*(?:=|\?|equals?)/i,
        /how\s+many\s+(\d+)s?\s+in\s+(\d+)/i,
        /share\s+(\d+)\s+(?:among|between)\s+(\d+)/i,
        
        // Nepali (Romanized)
        /(\d+)\s+lai\s+(\d+)\s+le\s+bhag\s+garda/i,
        /(\d+)\s+ma\s+(\d+)\s+kati\s+choti/i,
        /(\d+)\s*÷\s*(\d+)\s+kati/i,
        /(\d+)\s+bhag\s+(\d+)\s+kati/i
      ],
      operation: (a, b) => b !== 0 ? a / b : null,
      symbol: '÷'
    },
    
    // Advanced mathematical operations
    square: {
      patterns: [
        // English
        /(?:square\s+of\s+|(\d+)\s*(?:squared|\^2|²))/i,
        /(\d+)\s+square/i,
        
        // Nepali
        /(\d+)\s*ko\s*square\s*garda/i,
        /(\d+)\s*ko\s*square/i,
        /(\d+)\s+square\s+kati/i
      ],
      operation: (a) => a * a,
      symbol: '²'
    },
    
    squareRoot: {
      patterns: [
        // English
        /(?:square\s+root\s+of\s+|√)(\d+)/i,
        /sqrt\s*\(\s*(\d+)\s*\)/i,
        
        // Nepali
        /(\d+)\s*ko\s*square\s*root/i,
        /(\d+)\s+ko\s+root/i,
        /(\d+)\s+square\s+root\s+kati/i
      ],
      operation: (a) => Math.sqrt(a),
      symbol: '√'
    },
    
    power: {
      patterns: [
        /(\d+)\s*(?:to\s+the\s+power\s+of\s+|raised\s+to\s+|\^)(\d+)/i,
        /(\d+)\s*(?:ka|ke)\s*(\d+)\s*power/i, // Hindi
        /(\d+)\s*elevado\s+a\s+(\d+)/i, // Spanish
        /(\d+)\s*puissance\s+(\d+)/i, // French
        /(\d+)\s*hoch\s+(\d+)/i // German
      ],
      operation: (a, b) => Math.pow(a, b),
      symbol: '^'
    },
    
    percentage: {
      patterns: [
        /what\s+is\s+(\d+)%\s+of\s+(\d+)/i,
        /(\d+)\s*percent\s+of\s+(\d+)/i,
        /(\d+)%\s*ka\s*(\d+)/i, // Hindi
        /(\d+)\s*por\s*ciento\s*de\s*(\d+)/i, // Spanish
        /(\d+)\s*pour\s*cent\s*de\s*(\d+)/i, // French
        /(\d+)\s*prozent\s*von\s*(\d+)/i // German
      ],
      operation: (a, b) => (a / 100) * b,
      symbol: '%'
    }
  };

  // Enhanced Pattern recognition function
  const findPattern = (numbers) => {
    if (numbers.length < 2) return null;
    
    console.log('Finding pattern for:', numbers);
    
    // 1. Check for digit repetition patterns (1, 11, 111, 1111...)
    const digitPatterns = checkDigitPatterns(numbers);
    if (digitPatterns) {
      console.log('Found digit pattern:', digitPatterns);
      return digitPatterns;
    }
    
    // 2. Check for Fibonacci sequence
    const fibPattern = checkFibonacci(numbers);
    if (fibPattern) {
      console.log('Found Fibonacci pattern:', fibPattern);
      return fibPattern;
    }
    
    // 3. Check arithmetic sequence (constant difference)
    const differences = [];
    for (let i = 1; i < numbers.length; i++) {
      differences.push(numbers[i] - numbers[i-1]);
    }
    
    const isArithmetic = differences.every(diff => diff === differences[0]);
    if (isArithmetic && numbers.length >= 3) {
      const commonDiff = differences[0];
      const nextNumber = numbers[numbers.length - 1] + commonDiff;
      console.log('Found arithmetic pattern:', { commonDiff, nextNumber });
      return {
        type: 'arithmetic',
        pattern: `Add ${commonDiff} each time`,
        next: nextNumber,
        explanation: `${numbers.join(', ')} → each number increases by ${commonDiff}`,
        sequence: `${numbers.join(', ')}, ${nextNumber}`
      };
    }
    
    // 4. Check geometric sequence (constant ratio)
    if (numbers.every(n => n !== 0) && numbers.length >= 3) {
      const ratios = [];
      for (let i = 1; i < numbers.length; i++) {
        ratios.push(numbers[i] / numbers[i-1]);
      }
      
      const isGeometric = ratios.every(ratio => Math.abs(ratio - ratios[0]) < 0.001);
      if (isGeometric) {
        const commonRatio = ratios[0];
        const nextNumber = numbers[numbers.length - 1] * commonRatio;
        console.log('Found geometric pattern:', { commonRatio, nextNumber });
        return {
          type: 'geometric',
          pattern: `Multiply by ${commonRatio} each time`,
          next: Math.round(nextNumber * 100) / 100,
          explanation: `${numbers.join(', ')} → each number is multiplied by ${commonRatio}`,
          sequence: `${numbers.join(', ')}, ${Math.round(nextNumber * 100) / 100}`
        };
      }
    }
    
    // 5. Check for perfect squares sequence
    const squares = numbers.map(n => Math.sqrt(n));
    const isSquares = squares.every(s => Number.isInteger(s) && s > 0);
    if (isSquares && numbers.length >= 3) {
      // Check if it's consecutive squares
      const isConsecutive = squares.every((s, i) => i === 0 || s === squares[i-1] + 1);
      if (isConsecutive) {
        const nextSquareRoot = squares[squares.length - 1] + 1;
        const nextNumber = nextSquareRoot * nextSquareRoot;
        console.log('Found perfect squares pattern:', nextNumber);
        return {
          type: 'squares',
          pattern: `Perfect squares: ${squares[0]}², ${squares[1]}², ${squares[2]}², ...`,
          next: nextNumber,
          explanation: `${numbers.join(', ')} → consecutive perfect squares`,
          sequence: `${numbers.join(', ')}, ${nextNumber}`
        };
      }
    }
    
    // 6. Check for powers of 2 (2, 4, 8, 16...)
    const powersOf2 = checkPowersOf2(numbers);
    if (powersOf2) {
      console.log('Found powers of 2 pattern:', powersOf2);
      return powersOf2;
    }
    
    // 7. Check for triangular numbers (1, 3, 6, 10, 15...)
    const triangular = checkTriangularNumbers(numbers);
    if (triangular) {
      console.log('Found triangular pattern:', triangular);
      return triangular;
    }
    
    console.log('No pattern found');
    return null;
  };
  
  // Helper function to check digit repetition patterns
  const checkDigitPatterns = (numbers) => {
    const strings = numbers.map(n => n.toString());
    
    // Check for single digit repetition (1, 11, 111, 1111...)
    const firstDigit = strings[0][0];
    const isRepeatingDigit = strings.every((str, i) => {
      const expectedLength = i + 1;
      return str.length === expectedLength && str === firstDigit.repeat(expectedLength);
    });
    
    if (isRepeatingDigit) {
      const nextLength = strings.length + 1;
      const nextNumber = parseInt(firstDigit.repeat(nextLength));
      return {
        type: 'digit_repetition',
        pattern: `Repeat digit "${firstDigit}" increasing length`,
        next: nextNumber,
        explanation: `${numbers.join(', ')} → each number adds another "${firstDigit}"`,
        sequence: `${numbers.join(', ')}, ${nextNumber}`
      };
    }
    
    // Check for alternating digits (12, 121, 1212...)
    if (strings.length >= 2) {
      const pattern = strings[0];
      const isAlternating = strings.every((str, i) => {
        const expectedStr = pattern.repeat(Math.ceil(str.length / pattern.length)).substring(0, str.length);
        return str === expectedStr;
      });
      
      if (isAlternating && strings.every((str, i) => i === 0 || str.length > strings[i-1].length)) {
        const nextLength = strings[strings.length - 1].length + pattern.length;
        const nextNumber = parseInt(pattern.repeat(Math.ceil(nextLength / pattern.length)).substring(0, nextLength));
        return {
          type: 'alternating_digits',
          pattern: `Repeat pattern "${pattern}"`,
          next: nextNumber,
          explanation: `${numbers.join(', ')} → repeating pattern "${pattern}"`,
          sequence: `${numbers.join(', ')}, ${nextNumber}`
        };
      }
    }
    
    return null;
  };
  
  // Helper function to check Fibonacci sequence
  const checkFibonacci = (numbers) => {
    if (numbers.length < 3) return null;
    
    const isFibonacci = numbers.every((num, i) => {
      if (i < 2) return true;
      return num === numbers[i-1] + numbers[i-2];
    });
    
    if (isFibonacci) {
      const nextNumber = numbers[numbers.length - 1] + numbers[numbers.length - 2];
      return {
        type: 'fibonacci',
        pattern: 'Fibonacci sequence (each number = sum of previous two)',
        next: nextNumber,
        explanation: `${numbers.join(', ')} → Fibonacci: ${numbers[numbers.length-2]} + ${numbers[numbers.length-1]} = ${nextNumber}`,
        sequence: `${numbers.join(', ')}, ${nextNumber}`
      };
    }
    
    return null;
  };
  
  // Helper function to check powers of 2
  const checkPowersOf2 = (numbers) => {
    if (numbers.length < 3) return null;
    
    const isPowersOf2 = numbers.every((num, i) => {
      const expectedPower = Math.pow(2, i + 1);
      return num === expectedPower;
    });
    
    if (isPowersOf2) {
      const nextPower = numbers.length + 1;
      const nextNumber = Math.pow(2, nextPower);
      return {
        type: 'powers_of_2',
        pattern: 'Powers of 2: 2¹, 2², 2³, ...',
        next: nextNumber,
        explanation: `${numbers.join(', ')} → powers of 2: next is 2^${nextPower} = ${nextNumber}`,
        sequence: `${numbers.join(', ')}, ${nextNumber}`
      };
    }
    
    return null;
  };
  
  // Helper function to check triangular numbers
  const checkTriangularNumbers = (numbers) => {
    if (numbers.length < 3) return null;
    
    const isTriangular = numbers.every((num, i) => {
      const n = i + 1;
      const triangularNum = (n * (n + 1)) / 2;
      return num === triangularNum;
    });
    
    if (isTriangular) {
      const nextN = numbers.length + 1;
      const nextNumber = (nextN * (nextN + 1)) / 2;
      return {
        type: 'triangular',
        pattern: 'Triangular numbers: n(n+1)/2',
        next: nextNumber,
        explanation: `${numbers.join(', ')} → triangular numbers: next is ${nextN}×${nextN+1}/2 = ${nextNumber}`,
        sequence: `${numbers.join(', ')}, ${nextNumber}`
      };
    }
    
    return null;
  };

  // Language detection for English and Nepali only
  const detectLanguage = (text) => {
    const nepaliKeywords = [
      'kati', 'ra', 'ma', 'lai', 'le', 'garda', 'bhane', 'dekhi', 'choti',
      'jodda', 'ghatayo', 'bhag', 'guna', 'ko', 'square', 'root'
    ];

    const lowerText = text.toLowerCase();
    
    // Check for Nepali keywords
    for (const keyword of nepaliKeywords) {
      if (lowerText.includes(keyword)) {
        return 'nepali';
      }
    }
    
    // Default to English if no Nepali keywords found
    return 'english';
  };

  // Enhanced math solver with improved pattern matching
  const solveMathProblem = (text) => {
    const detectedLang = detectLanguage(text);
    setDetectedLanguage(detectedLang);

    console.log('Solving math problem:', text);
    console.log('Detected language:', detectedLang);

    // Handle unit conversions first
    if (text.toLowerCase().includes('milliliters') && text.toLowerCase().includes('liters')) {
      const literMatch = text.match(/(\d+(?:\.\d+)?)\s*liters?/i);
      if (literMatch) {
        const liters = parseFloat(literMatch[1]);
        const milliliters = liters * 1000;
        const explanation = `${liters} L × 1000 = ${milliliters} ml`;
        return getLocalizedMathResponse(explanation, `${milliliters} ml`, detectedLang, 'unit_conversion');
      }
      
      const mlMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:milliliters?|ml)/i);
      if (mlMatch) {
        const milliliters = parseFloat(mlMatch[1]);
        const liters = milliliters / 1000;
        const explanation = `${milliliters} ml ÷ 1000 = ${liters} L`;
        return getLocalizedMathResponse(explanation, `${liters} L`, detectedLang, 'unit_conversion');
      }
    }

    // Handle pattern recognition with improved matching
    const patternMatches = [
      text.match(/pattern[:\s]*(\d+(?:\s*,\s*\d+)*)/i),
      text.match(/what\s+comes\s+next[:\s]*(\d+(?:\s*,\s*\d+)*)/i),
      text.match(/(\d+(?:\s*,\s*\d+)*)\s*,?\s*___?\s*\??/i),
      text.match(/(\d+(?:\s*,\s*\d+)*)\s*,?\s*\?\s*$/i),
      text.match(/find\s+(?:the\s+)?next\s+(?:number|term)[:\s]*(\d+(?:\s*,\s*\d+)*)/i),
      text.match(/sequence[:\s]*(\d+(?:\s*,\s*\d+)*)/i),
      text.match(/(\d+(?:\s*,\s*\d+)*)\s+what\s+comes\s+next/i)
    ];
    
    const patternMatch = patternMatches.find(match => match !== null);
    
    if (patternMatch || text.toLowerCase().includes('what comes next') || text.toLowerCase().includes('pattern')) {
      // Extract all numbers from the text
      const numberMatches = text.match(/\d+/g);
      if (numberMatches && numberMatches.length >= 2) {
        const numbers = numberMatches.map(n => parseInt(n));
        console.log('Pattern analysis for numbers:', numbers);
        
        const pattern = findPattern(numbers);
        
        if (pattern) {
          const explanation = `🔍 Pattern: ${pattern.pattern}\n📊 Sequence: ${pattern.sequence}\n💡 ${pattern.explanation}`;
          return getLocalizedMathResponse(explanation, pattern.next, detectedLang, 'pattern');
        } else {
          // If no pattern found, provide helpful feedback
          const fallbackMsg = detectedLang === 'nepali' 
            ? `🤔 Yo pattern ma ${numbers.join(', ')} numbers chan tara ma pattern identify garna sakina. Aru examples try garnus jastai: 2, 4, 6 ya 1, 4, 9, 16`
            : `🤔 I can see the numbers ${numbers.join(', ')} but couldn't identify the pattern. Try examples like: 2, 4, 6 or 1, 4, 9, 16`;
          return fallbackMsg;
        }
      }
    }

    // Try each mathematical operation
    for (const [operation, config] of Object.entries(mathPatterns)) {
      if (operation === 'unitConversion' || operation === 'patterns') continue; // Already handled above
      
      console.log('Trying operation:', operation);
      
      for (let i = 0; i < config.patterns.length; i++) {
        const pattern = config.patterns[i];
        console.log('Testing pattern:', pattern);
        
        const match = text.match(pattern);
        if (match) {
          console.log('Pattern matched:', match);
          
          try {
            let result;
            let explanation = '';
            
            if (operation === 'square') {
              const num = parseFloat(match[1]);
              result = config.operation(num);
              explanation = `${num}² = ${num} × ${num} = ${result}`;
            } else if (operation === 'squareRoot') {
              const num = parseFloat(match[1]);
              result = config.operation(num);
              explanation = `√${num} = ${result}`;
            } else if (operation === 'power') {
              const base = parseFloat(match[1]);
              const exp = parseFloat(match[2]);
              result = config.operation(base, exp);
              explanation = `${base}^${exp} = ${result}`;
            } else if (operation === 'percentage') {
              const percent = parseFloat(match[1]);
              const of = parseFloat(match[2]);
              result = config.operation(percent, of);
              explanation = `${percent}% of ${of} = (${percent}/100) × ${of} = ${result}`;
            } else {
              // Basic arithmetic
              const num1 = parseFloat(match[1]);
              const num2 = parseFloat(match[2]);
              
              console.log('Numbers found:', num1, num2);
              
              if (operation === 'division' && num2 === 0) {
                return getLocalizedResponse('divisionByZero', detectedLang);
              }
              
              result = config.operation(num1, num2);
              explanation = `${num1} ${config.symbol} ${num2} = ${result}`;
            }

            console.log('Calculation result:', result);
            console.log('Explanation:', explanation);

            // Format result with localized response
            return getLocalizedMathResponse(explanation, result, detectedLang, operation);
          } catch (error) {
            console.error('Calculation error:', error);
            return getLocalizedResponse('calculationError', detectedLang);
          }
        }
      }
    }

    // If no pattern matches, try simple number extraction with operation detection
    const numbers = text.match(/\d+(?:\.\d+)?/g);
    if (numbers && numbers.length >= 2) {
      const num1 = parseFloat(numbers[0]);
      const num2 = parseFloat(numbers[1]);
      const lowerText = text.toLowerCase();
      
      console.log('Fallback: extracted numbers:', num1, num2);
      console.log('Text for operation detection:', lowerText);
      
      // Try to detect operation from text
      let result, operation, symbol;
      
      if (lowerText.includes('×') || lowerText.includes('*') || 
          lowerText.includes('time') || lowerText.includes('multipl') ||
          lowerText.includes('guna')) {
        result = num1 * num2;
        operation = 'multiplication';
        symbol = '×';
      } else if (lowerText.includes('-') || lowerText.includes('minus') || 
                 lowerText.includes('subtract') || lowerText.includes('ghat')) {
        result = num1 - num2;
        operation = 'subtraction';
        symbol = '-';
      } else if (lowerText.includes('÷') || lowerText.includes('/') || 
                 lowerText.includes('divide') || lowerText.includes('bhag')) {
        if (num2 !== 0) {
          result = num1 / num2;
          operation = 'division';
          symbol = '÷';
        } else {
          return getLocalizedResponse('divisionByZero', detectedLang);
        }
      } else {
        // Default to addition
        result = num1 + num2;
        operation = 'addition';
        symbol = '+';
      }
      
      const explanation = `${num1} ${symbol} ${num2} = ${result}`;
      console.log('Fallback calculation:', explanation);
      
      return getLocalizedMathResponse(explanation, result, detectedLang);
    }

    console.log('No math pattern found');
    return null;
  };

  // Localized responses for English and Nepali only
  const getLocalizedResponse = (key, lang) => {
    const responses = {
      divisionByZero: {
        english: "🚫 Cannot divide by zero! That's undefined in mathematics.",
        nepali: "🚫 Zero le bhag garna mildaina! Yo mathematics ma undefined ho."
      },
      calculationError: {
        english: "❌ Sorry, I encountered an error in calculation. Please try again!",
        nepali: "❌ Maaf garnus, calculation ma error aayo. Pheri try garnus!"
      },
      needClarification: {
        english: "🤔 I can see numbers but need clarification on the operation.",
        nepali: "🤔 Ma numbers dekhna sakchu tara operation clear chaina."
      },
      foundNumbers: {
        english: "Found numbers",
        nepali: "Bhetiyeka numbers"
      }
    };

    return responses[key]?.[lang] || responses[key]?.english || "Response not available";
  };

  const getLocalizedMathResponse = (explanation, result, lang, type = 'arithmetic') => {
    const templates = {
      english: {
        arithmetic: [
          `✨ ${explanation}`,
          `🎯 The answer is ${result}! (${explanation})`,
          `💡 Here's the solution: ${explanation}`,
          `🌟 ${explanation} ✅`,
          `🧮 Calculation: ${explanation}`
        ],
        unit_conversion: [
          `🔄 Unit Conversion: ${explanation}`,
          `📏 ${explanation} ✅`,
          `🎯 Converted: ${explanation}`,
          `💡 ${explanation}`
        ],
        pattern: [
          `🔍 Pattern Analysis:\n${explanation}`,
          `🧩 Pattern Found!\n${explanation}`,
          `📊 Sequence Pattern:\n${explanation}`,
          `🎯 Next Number: ${result}\n${explanation}`
        ]
      },
      nepali: {
        arithmetic: [
          `✨ ${explanation}`,
          `🎯 Answer ${result} ho! (${explanation})`,
          `💡 Solution yo ho: ${explanation}`,
          `🌟 ${explanation} ✅`,
          `🧮 Calculation: ${explanation}`
        ],
        unit_conversion: [
          `🔄 Unit Conversion: ${explanation}`,
          `📏 ${explanation} ✅`,
          `🎯 Convert bhayo: ${explanation}`,
          `💡 ${explanation}`
        ],
        pattern: [
          `🔍 Pattern Analysis:\n${explanation}`,
          `🧩 Pattern bhetyo!\n${explanation}`,
          `📊 Sequence Pattern:\n${explanation}`,
          `🎯 Arko Number: ${result}\n${explanation}`
        ]
      }
    };

    const langTemplates = templates[lang] || templates.english;
    const typeTemplates = langTemplates[type] || langTemplates.arithmetic;
    return typeTemplates[Math.floor(Math.random() * typeTemplates.length)];
  };

  // Enhanced bot response with AI-like capabilities
  const getBotResponse = async (userMessage) => {
    setIsProcessing(true);
    
    const lowerMessage = userMessage.toLowerCase();
    
    // Try to solve math problem first (local processing)
    const mathSolution = solveMathProblem(userMessage);
    if (mathSolution) {
      setIsProcessing(false);
      return mathSolution;
    }

    // Try AI backend for enhanced processing
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/ai/solve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: userMessage
        })
      });

      if (response.ok) {
        const aiResult = await response.json();
        if (aiResult.success) {
          setIsProcessing(false);
          return aiResult.formatted_response;
        }
      }
    } catch (error) {
      console.log('AI backend not available, using local processing');
    }

    // Greeting responses in English and Nepali
    if (lowerMessage.match(/\b(hi|hello|hey|namaste|namaskar)\b/)) {
      const greetings = {
        english: "Hello! 👋 I'm your AI Math Assistant. I can solve problems in English and Nepali!",
        nepali: "Namaste! 👋 Ma timro AI Math Assistant hun. Ma English ra Nepali ma problems solve garna sakchu!"
      };
      
      const detectedLang = detectLanguage(userMessage);
      setIsProcessing(false);
      return greetings[detectedLang] || greetings.english;
    }

    // Help responses
    if (lowerMessage.match(/\b(help|maddat|sahayata)\b/)) {
      const helpTexts = {
        english: `🤖 I can help you with:
• Basic arithmetic (+ - × ÷)
• Unit conversions (liters ↔ ml)
• Pattern recognition (3, 9, 15, ___?)
• Square roots and powers
• Percentages
• English and Nepali support
• Natural language math questions

Try asking: "What is 15 × 8?" or "1.5 liters to ml?" or "Pattern: 2, 4, 6, ___?"`,
        nepali: `🤖 Ma timro maddat garna sakchu:
• Basic arithmetic (+ - × ÷)
• Unit conversions (liters ↔ ml)
• Pattern recognition (3, 9, 15, ___?)
• Square roots ra powers
• Percentages
• English ra Nepali support
• Natural language math questions

Try garnus: "15 × 8 kati?" ya "1.5 liter ma kati ml?" ya "Pattern: 2, 4, 6, ___?"`
      };
      
      const detectedLang = detectLanguage(userMessage);
      setIsProcessing(false);
      return helpTexts[detectedLang] || helpTexts.english;
    }

    // Default response for unrecognized input
    const defaultResponses = {
      english: "🤔 I didn't understand that math question. Try asking something like 'What is 5 + 3?' or 'Square root of 25'",
      nepali: "🤔 Maile yo math question bujhina. Yesto sodhnus jastai '5 + 3 kati?' wa '25 ko square root'"
    };

    const detectedLang = detectLanguage(userMessage);
    setIsProcessing(false);
    return defaultResponses[detectedLang] || defaultResponses.english;
  };

  // Save chat history
  useEffect(() => {
    localStorage.setItem('enhanced_mathbot_history', JSON.stringify(messages));
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isProcessing) return;

    const userMessage = { type: 'user', text: input, time: new Date() };
    setMessages(prev => [...prev, userMessage]);

    const botResponse = await getBotResponse(input);
    const botMessage = { type: 'bot', text: botResponse, time: new Date() };
    setMessages(prev => [...prev, botMessage]);

    setInput('');
  };

  const quickQuestions = [
    'What is 25 × 4?',
    'How many milliliters are in 1.5 liters?',
    'Pattern: 1, 11, 111, ___?',
    'What comes next: 3, 9, 15, 21, ___?',
    'Square root of 144',
    'Help me! 🆘'
  ];

  const multilingualExamples = [
    { lang: 'English', text: 'What is 12 + 8?' },
    { lang: 'English', text: 'Pattern: 1, 11, 111, ___?' },
    { lang: 'English', text: 'Pattern: 2, 4, 8, ___?' },
    { lang: 'Nepali', text: '10 ra 6 jodda kati?' }
  ];

  return (
    <>
      {/* Enhanced Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-2 sm:right-4 bg-gradient-to-r from-purple-600 via-blue-600 to-green-500 text-white p-3 sm:p-4 rounded-full shadow-2xl hover:scale-110 transform transition-all duration-300 animate-pulse"
        >
          <div className="flex items-center justify-center">
            <span className="text-xl sm:text-2xl">🧠</span>
            <span className="text-xs ml-1">AI</span>
          </div>
        </button>
      )}

      {/* Enhanced Chat Window */}
      {isOpen && (
        <div className="fixed bottom-2 right-2 left-2 sm:bottom-4 sm:right-4 sm:left-auto w-auto sm:w-80 md:w-96 h-[calc(100vh-1rem)] sm:h-[600px] max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)] bg-white rounded-xl sm:rounded-2xl shadow-2xl flex flex-col z-50 border border-gray-200">
          {/* Enhanced Header */}
          <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-green-500 text-white p-3 sm:p-4 rounded-t-xl sm:rounded-t-2xl">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="relative">
                  <span className="text-xl sm:text-2xl">🧠</span>
                  <span className="absolute -top-1 -right-1 bg-green-400 text-xs px-1 rounded-full">AI</span>
                </div>
                <div>
                  <div className="font-bold text-sm sm:text-base">MathVenture_AI</div>
                  <div className="text-xs opacity-90 flex items-center gap-2">
                    {isProcessing ? (
                      <>🔄 Processing...</>
                    ) : (
                      <>
                        ✅ Ready | 🌍 {detectedLanguage.toUpperCase()}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center transition-all text-sm sm:text-base"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2 sm:space-y-3 bg-gradient-to-b from-gray-50 to-white">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[90%] sm:max-w-[85%] p-2 sm:p-3 rounded-xl sm:rounded-2xl ${
                    msg.type === 'user'
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-br-none shadow-lg'
                      : 'bg-white text-gray-800 rounded-bl-none shadow-lg border border-gray-100'
                  }`}
                >
                  <div className="text-xs sm:text-sm whitespace-pre-line">{msg.text}</div>
                  <div className={`text-xs mt-1 opacity-70 ${msg.type === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                    {msg.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
            {isProcessing && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 rounded-xl sm:rounded-2xl rounded-bl-none shadow-lg border border-gray-100 p-2 sm:p-3">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin">🧠</div>
                    <span className="text-xs sm:text-sm">AI is thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length <= 2 && (
            <div className="p-2 sm:p-3 border-t bg-gradient-to-r from-purple-50 to-blue-50">
              <div className="text-xs text-gray-600 mb-2 font-semibold">🚀 Try these:</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 mb-3">
                {quickQuestions.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setInput(q);
                      setTimeout(() => handleSend(), 100);
                    }}
                    className="text-xs bg-white text-blue-600 px-2 py-1 rounded-lg hover:bg-blue-50 border border-blue-200 transition-all text-left"
                  >
                    {q}
                  </button>
                ))}
              </div>
              

            </div>
          )}

          {/* Enhanced Input */}
          <div className="p-2 sm:p-4 border-t bg-white rounded-b-xl sm:rounded-b-2xl">
            <div className="flex gap-1 sm:gap-2 mb-2 flex-wrap">
              <button
                onClick={() => {
                  if (window.confirm('Clear chat history?')) {
                    setMessages([
                      { 
                        type: 'bot', 
                        text: 'Chat cleared! 🧹 I\'m ready for new math questions in English and Nepali! 🌍', 
                        time: new Date() 
                      }
                    ]);
                    localStorage.removeItem('enhanced_mathbot_history');
                  }
                }}
                className="text-xs px-2 sm:px-3 py-1 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
              >
                🧹 Clear
              </button>
              
              <div className="text-xs px-2 sm:px-3 py-1 rounded-full bg-green-100 text-green-700">
                🌍 {detectedLanguage.toUpperCase()}
              </div>
            </div>
            
            <div className="flex gap-1 sm:gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !isProcessing && handleSend()}
                placeholder="Ask math questions... 🧮"
                className="flex-1 border border-gray-300 rounded-full px-3 sm:px-4 py-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                disabled={isProcessing}
              />
              <button
                onClick={handleSend}
                disabled={isProcessing || !input.trim()}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 sm:px-6 py-2 rounded-full hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {isProcessing ? '🔄' : '🚀'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EnhancedMathChatbot;